/**
 * The help page (Kawi, 6 Aug: "a page where everything is explained, how
 * the inventory works etc"). Plain words, short sections, no jargon —
 * every sentence here must match what the app actually does; when
 * behavior changes, this page changes in the same PR (same rule as
 * docs/specs/inventory-document-read.md, the spec behind this page).
 *
 * Rewritten 7 Aug on his order ("its a long text now, make it more easy
 * to grasp"): every section now opens with ONE bold sentence that says
 * the whole thing. The paragraphs under it are detail a reader can skip.
 * Same content, shorter, plus the four research angles he asked to
 * include. Three claims were also CORRECTED against the code after a
 * Codex check: what 'review needed' covers, what a fact change actually
 * triggers, and the example-disappears rule that the first rewrite lost.
 */
import React from 'react';

const H = ({ children }: { children: React.ReactNode }) => (
    <p className="text-[11px] font-semibold tracking-[0.06em] uppercase text-fg-tertiary mt-8 mb-2">{children}</p>
);
const P = ({ children }: { children: React.ReactNode }) => (
    <p className="text-[13.5px] text-fg-secondary leading-relaxed mb-2 max-w-[72ch]">{children}</p>
);
const B = ({ children }: { children: React.ReactNode }) => (
    <b className="font-semibold text-fg">{children}</b>
);
/* the one sentence that carries a section — a reader who reads only
   these lines still understands the product */
const Lead = ({ children }: { children: React.ReactNode }) => (
    <p className="text-[15px] text-fg font-semibold leading-snug mb-2.5 max-w-[68ch]">{children}</p>
);
/* a compact labelled row — the label carries the meaning, so the eye can
   skip the rest */
const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <p className="grid grid-cols-[132px_1fr] gap-x-4 items-baseline text-[13.5px] text-fg-secondary leading-relaxed mb-1.5 max-w-[74ch]">
        <span className="font-semibold text-fg">{label}</span>
        <span>{children}</span>
    </p>
);

export default function InventoryHelp({ onBack }: { onBack: () => void }) {
    return (
        <div className="max-w-[860px] mx-auto px-7 pt-[30px] pb-[60px] font-body text-fg">
            <div className="flex gap-2 items-center text-[13px] text-fg-tertiary mb-4">
                <button onClick={onBack} className="text-link">← Back</button>
                <span className="text-border-strong">·</span>
                <span className="text-fg font-semibold">How the inventory works</span>
            </div>
            <h1 className="font-heading text-[32px] leading-tight text-primary mb-1">How the inventory works</h1>
            <p className="text-[13px] text-fg-tertiary mb-5">
                The whole product in one line: BARK proposes, you decide, and
                nothing counts until a named person signs it.</p>

            <H>The four places</H>
            <Row label="Clusters">areas of regulation — Large Exposures, IRRBB. Each holds
                the individual duties, called obligations.</Row>
            <Row label="Register">everything your bank holds: governing documents, models,
                data, filings. One entry can answer several obligations.</Row>
            <Row label="Map">the whole estate in one picture — your documents, the
                obligations they answer, the laws behind them.</Row>
            <Row label="Actions">the one inbox. If something waits for a named person, it is
                there and nowhere else.</Row>

            <H>The trust rules</H>
            <Lead>Nothing counts until a named person signs it.</Lead>
            <P>Every signature becomes a record that is never deleted. Withdrawing
                something writes a new record; it never erases the old one.</P>
            <P><B>Examples count as nothing.</B> BARK ships example documents as starting
                points. They are marked, they answer nothing, and they never become your
                content by themselves. An example leaves a cluster as soon as your own
                documents cover what it stood for.</P>
            <P>Everything BARK proposes is a <B>suggestion</B> until you accept it with your
                name. Dismiss one and it stays dismissed — it never comes back.</P>

            <H>How BARK finds every obligation</H>
            <Lead>Every cluster is researched from four directions, so gaps show up.</Lead>
            <Row label="By instrument">law by law — CRR, CRD, Swedish law, FFFS, EBA
                guidelines.</Row>
            <Row label="By activity">follow the bank's actual work end to end, and ask what
                rules govern each step.</Row>
            <Row label="External outputs">anything that leaves the bank or has a hard
                consequence — reports, filings, disclosures, deadlines, breaches.</Row>
            <Row label="Tail hunt">assume the first three missed things, and hunt the edges:
                definitions, exceptions, size thresholds, national add-ons.</Row>
            <P>The four lists are then merged, and anything only one angle found gets extra
                scrutiny — a duty seen from a single direction is the one most likely to be
                wrong.</P>

            <H>Adding a document</H>
            <Lead>Drop the file. BARK prepares the act. You sign once.</Lead>
            <P>One file or twenty at a time. BARK reads each one <B>before anything is
                signed</B> and proposes a name, a kind, and the obligations it answers —
                each with the document's own sentence as evidence. Untick what is wrong,
                add what is missing, sign.</P>
            <P>If a file cannot be read (a scan, an unusual format), nothing breaks: you
                name it and pick yourself. Readable today: txt, md, csv, and pdf with real
                text.</P>

            <H>What happens after you sign</H>
            <Lead>BARK reads the document again against your whole inventory, and brings
                back suggestions — never changes.</Lead>
            <Row label="Also answers…">the document seems to answer one more obligation.
                Accepting is a signed link, one click.</Row>
            <Row label="Says something">it names a fact your profile holds. Empty field →
                one-click "Use this". Different value → both sides shown, you choose.
                Same value → nothing at all; silence is the answer when nothing needs
                you.</Row>
            <P>The document's page always says where the read stands: reading, done with
                suggestions, done with nothing to say, or failed with a retry hint.</P>

            <H>How your documents relate</H>
            <Lead>A document can be governed by another one — the appetite above the
                limits, the limits above the policy.</Lead>
            <P>BARK proposes these connections when a document says so in its own words,
                and you can set one by hand. Each connection is a signed act, and the
                Register's Hierarchy view draws the resulting tree.</P>

            <H>The institution profile</H>
            <Lead>Your bank's master data decides what applies to you.</Lead>
            <P>Country, type, size, business lines. Obligations read these facts. Change a
                fact and every obligation reading it is flagged for review at once. When
                the change comes through the review queue — a suggestion you approve —
                BARK also re-reads what those obligations now mean for you.</P>
            <P>When a reading concludes an obligation <B>does not apply</B>, it arrives in
                Actions as a prepared removal — the reading is the written reason, and
                removing stays your signed decision. "Keep it" buries it for good.</P>

            <H>The status words</H>
            <Lead>An obligation is exactly one of four things.</Lead>
            <Row label="answered">a named person signed a real document onto it.</Row>
            <Row label="open">nothing signed yet.</Row>
            <Row label="review needed">something moved under it — the law, your profile,
                or your group structure. The flag stands until someone clears it.</Row>
            <Row label="dismissed">your bank removed it, with a written reason, on record.</Row>
            <P>We never say "compliant". A signed document proves an answer exists —
                judging compliance is bigger than that, and this product does not pretend
                otherwise.</P>

            <H>If something looks wrong</H>
            <P>Every page carries "Something wrong here? Ask for a review…" — your words go
                to Actions with your name, and a person looks at it.</P>
        </div>
    );
}
